'use client';

import React from 'react';

import { resolveAuthTokenSync } from '@/services/httpServices/authTokenResolver';
import type { HttpServiceConfig } from '@/services/httpServices/types';
import { useIsSpaceHydrated, useSpaceId } from '@/stores/space';
import { getTargetEnvironmentClient } from '@/utils/target-detection';

/**
 * Query configuration result with space dependency enforcement
 */
export type QueryConfigResult = {
  /** HTTP request config with current space from store */
  httpRequestConfig: HttpServiceConfig;
  /** Whether queries should be enabled (space must exist and be hydrated) */
  isEnabled: boolean;
  /** Current space ID */
  spaceId: string;
  /** Whether space is available */
  hasSpace: boolean;
  /** Whether space store is hydrated */
  isHydrated: boolean;
};

/**
 * Centralized hook for query configuration
 *
 * Enforces space as a blocking dependency for all queries:
 * - Uses space store as single source of truth (synced from URL path)
 * - Returns isEnabled=false when no space is set or store not hydrated
 * - Provides consistent httpRequestConfig across all queries
 * - Automatically re-renders when space changes
 *
 * Usage:
 * ```tsx
 * const { httpRequestConfig, isEnabled } = useQueryConfig();
 *
 * const query = useQuery({
 *   queryKey: ['data', httpRequestConfig.space],
 *   queryFn: () => fetchData({ httpRequestConfig }),
 *   enabled: isEnabled,
 * });
 * ```
 */
export function useQueryConfig(): QueryConfigResult {
  // Get space from store (single source of truth, initialized from server props)
  const storeSpaceId = useSpaceId();
  const isHydrated = useIsSpaceHydrated();

  const config = React.useMemo(() => {
    // Always use store space - it's initialized from server props for SSR consistency
    const spaceId = storeSpaceId;
    const hasSpace = spaceId !== undefined && spaceId !== null && spaceId !== '';

    // Get target and auth token from cookies
    // These are available on client, empty values on server (but queries disabled anyway)
    const target = getTargetEnvironmentClient();
    const authToken = resolveAuthTokenSync();

    const httpRequestConfig: HttpServiceConfig = {
      space: spaceId,
      target,
      authToken,
    };

    return {
      httpRequestConfig,
      // Only enable queries after hydration to prevent SSR fetch attempts
      // Query keys are correct regardless, so HydrationBoundary cache works
      isEnabled: isHydrated === true && hasSpace === true,
      spaceId,
      hasSpace,
      isHydrated,
    };
  }, [storeSpaceId, isHydrated]);

  return config;
}

/**
 * Hook to check if queries should be enabled
 * Shorthand for cases where you only need the enabled state
 */
export function useQueryEnabled(): boolean {
  const { isEnabled } = useQueryConfig();
  return isEnabled;
}

/**
 * Hook to get current space ID from query config
 * Use this when you need just the space ID without full config
 * @deprecated Use useSpaceId from @/stores/space instead
 */
export function useSpaceFromCookie(): string {
  const { spaceId } = useQueryConfig();
  return spaceId;
}
